/**
 * Format a number as Nigerian Naira currency string.
 * @example naira(150000) => "₦150,000"
 */
export function naira(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '₦0';
  return `₦${num.toLocaleString()}`;
}

/**
 * Unit label for a hostel billing period.
 * @example billingPeriodUnit('MONTHLY') => "month"
 */
export function billingPeriodUnit(
  period?: string | null,
): 'month' | 'session' | 'extension period' {
  if (period === 'MONTHLY') return 'month';
  if (period === 'EXTENSION') return 'extension period';
  return 'session';
}

/**
 * Human-readable duration label for a hostel billing period.
 * @example billingPeriodLabel('EXTENSION') => "Per Extension Period"
 */
export function billingPeriodLabel(period?: string | null): string {
  if (period === 'MONTHLY') return 'Per Month';
  if (period === 'EXTENSION') return 'Per Extension Period';
  return 'Per Session';
}

/**
 * Wording for a room's session allocation, as students and admins read it.
 * @example roomGenderLabel('FEMALE') => "girls only"
 */
/**
 * Block name with its section, e.g. "Block A · Extension". Hostels reuse block
 * names across sections, so the name alone does not identify a block.
 */
export function blockLabel(blockName?: string | null, sectionName?: string | null): string {
  const name = blockName || '-';
  return sectionName ? `${name} · ${sectionName}` : name;
}

/**
 * "Block E" whether the stored name is "E" or already "Block E" — prefixing
 * blindly produced "Block Block E" on student screens.
 */
export function blockDisplayName(blockName?: string | null): string {
  const name = (blockName ?? '').trim();
  if (!name) return '';
  return /^block\b/i.test(name) ? name : `Block ${name}`;
}

/** "Block A · Main Building" for a student-facing location line. */
export function blockHeading(blockName?: string | null, sectionName?: string | null): string {
  return blockName ? blockLabel(blockDisplayName(blockName), sectionName) : '';
}

type BedLocationSource = {
  bedNumber?: string | null;
  room?: {
    roomNumber?: string | null;
    block?: { name?: string | null; section?: { name?: string | null } | null } | null;
  } | null;
} | null | undefined;

/**
 * Where a bed is, section first: "Male Hostel · Block E · Room 3 · Bed 4".
 * Block, room and bed numbers repeat across sections, so without the section
 * two different beds read identically.
 */
export function bedLocationLabel(bedSpace: BedLocationSource, separator = ' · '): string {
  const room = bedSpace?.room;
  const block = room?.block;
  return [
    block?.section?.name,
    blockDisplayName(block?.name),
    room?.roomNumber ? `Room ${room.roomNumber}` : '',
    bedSpace?.bedNumber ? `Bed ${bedSpace.bedNumber}` : '',
  ]
    .filter(Boolean)
    .join(separator);
}

const naturalOrder = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * Orders blocks by name, then by the hostel's section order, so each block
 * letter is followed by its twin in the next section:
 * Block A · Main Building, Block A · Extension, Block B · Main Building, ...
 */
export function sortBlocksBySection<T extends { name: string; sectionId?: string | null }>(
  blocks: T[],
  sections: { id: string; sortOrder?: number | null }[] = [],
): T[] {
  const rank = new Map(sections.map((section) => [section.id, section.sortOrder ?? 0]));
  const sectionRank = (block: T) => rank.get(block.sectionId ?? '') ?? Number.MAX_SAFE_INTEGER;
  return [...blocks].sort(
    (a, b) => naturalOrder.compare(a.name, b.name) || sectionRank(a) - sectionRank(b),
  );
}

/**
 * Who a bed is set aside for, mirroring the server rule: the room's own label
 * wins, then its section, then the hostel. Only an explicit MALE/FEMALE
 * restricts a bed, so missing data never locks a student out.
 */
export function bedAllocationGender(room?: {
  gender?: string | null;
  block?: {
    section?: { gender?: string | null } | null;
    hostel?: { gender?: string | null } | null;
  } | null;
}): "MALE" | "FEMALE" | "MIXED" {
  const restriction = (value?: string | null) =>
    value === "MALE" || value === "FEMALE" ? value : null;
  return (
    restriction(room?.gender) ??
    restriction(room?.block?.section?.gender) ??
    restriction(room?.block?.hostel?.gender) ??
    "MIXED"
  );
}

/**
 * Badge for a room set aside for one gender, as students see it while
 * booking. MIXED rooms get no badge.
 */
export function roomAllocationBadge(gender?: string | null) {
  if (gender === 'FEMALE') return { label: 'Girls only', variant: 'purple' as const };
  if (gender === 'MALE') return { label: 'Boys only', variant: 'info' as const };
  return null;
}

export function roomGenderLabel(gender?: string | null): string {
  if (gender === 'FEMALE') return 'girls only';
  if (gender === 'MALE') return 'boys only';
  return 'open to all';
}

/**
 * Format a date string or Date to a locale date string.
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString();
}

/**
 * Format a date string or Date to a locale date+time string.
 */
export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString();
}
